import { Container, LoadingScreen } from '@/shared/components'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ProductionRouteSummary from '@/features/production-routes/components/ProductionRouteSummary'
import {
  ProductionRouteUpdatePayload,
  productionRouteUpdatePayloadSchema
} from '@/features/production-routes/api/ProductionRouteModel'
import { BreadcrumbsBar, Button, H2 } from '@/common'
import { PencilIcon, TrashIcon } from '@/common/components/icons'
import { useDialog } from '@/shared/hooks/useDialog'
import ProductionRouteRenameDialog from './components/ProductionRouteRenameDialog'
import { useCallback, useMemo } from 'react'
import {
  useDeleteProductionRoute,
  useGetProductionRoute,
  useUpdateProductionRoute
} from '@/shared/hooks/api/productionRoutes'
import toast from 'react-hot-toast'
import { EmissionReportStatus } from '@/features/production-routes/api/EmissionReportModel'
import { Tooltip } from '@/common'
import ProductionRouteDeleteDialog from './components/ProductionRouteDeleteDialog'
import { useSession } from '@/shared/hooks/useSession'

export default function ProductionRouteViewPage() {
  const { t } = useTranslation()
  const organizationId = useSession().session.organization.id
  const productionRouteId = useParams<{ productionRouteId: string }>().productionRouteId!
  const navigate = useNavigate()
  const {
    data: productionRoute,
    isLoading,
    isSuccess
  } = useGetProductionRoute(organizationId, productionRouteId)
  const { mutateAsync: updateProductionRoute, isPending: isUpdatingProductionRoute } =
    useUpdateProductionRoute(organizationId, productionRouteId)
  const { mutateAsync: deleteProductionRoute, isPending: isDeletingProductionRoute } =
    useDeleteProductionRoute(organizationId, productionRouteId)
  const installationId = useParams<{ installationId: string }>().installationId!
  const reports = useMemo(() => productionRoute?.reports ?? [], [productionRoute])

  const {
    open: isRenameDialogOpen,
    openDialog: openRenameDialog,
    closeDialog: closeRenameDialog
  } = useDialog()
  const {
    open: isDeleteDialogOpen,
    openDialog: openDeleteDialog,
    closeDialog: closeDeleteDialog
  } = useDialog()

  const reportsNotDeleted = useMemo(() => {
    return reports.filter(report => !report.deletedAt)
  }, [reports])

  const onUpdateProductionRoute = useCallback(
    async (payload: ProductionRouteUpdatePayload) => {
      try {
        await updateProductionRoute(payload)
        toast.success(
          t('productionRoute:updateProductionRoute.notifications.updatedSuccessfully'),
          {
            position: 'bottom-center'
          }
        )
      } catch {
        toast.error(t('productionRoute:updateProductionRoute.notifications.errorUpdating'), {
          position: 'bottom-center'
        })
      }
    },
    [updateProductionRoute, t]
  )

  const onUpdateProductionRouteName = useCallback(
    (name: string) => {
      const validateState = productionRouteUpdatePayloadSchema.safeParse({
        ...productionRoute,
        name
      })
      if (!validateState.success) {
        return
      }

      onUpdateProductionRoute(validateState.data)
    },
    [productionRoute, onUpdateProductionRoute]
  )

  const submittedReports = useMemo(() => {
    return reportsNotDeleted.filter(report => report.status === EmissionReportStatus.SUBMITTED)
  }, [reportsNotDeleted])

  const draftReports = useMemo(() => {
    return reportsNotDeleted.filter(report => report.status === EmissionReportStatus.DRAFT)
  }, [reportsNotDeleted])

  const { tooltipString, disabled: isDeleteButtonDisabled } = useMemo(() => {
    if (submittedReports.length > 0) {
      return {
        tooltipString: t('productionRoute:updateProductionRoute.delete.tooltip.disabled'),
        disabled: true
      }
    }

    if (draftReports.length > 0) {
      return {
        tooltipString: t(
          'productionRoute:updateProductionRoute.delete.tooltip.disabledWithDraftReports',
          {
            reportsCount: draftReports.length
          }
        ),
        disabled: true
      }
    }

    return {
      tooltipString: '',
      disabled: false
    }
  }, [submittedReports, draftReports, t])

  const onDeleteProductionRoute = useCallback(async () => {
    await deleteProductionRoute()
    navigate(`/dashboard/${installationId}`)
  }, [deleteProductionRoute, navigate, installationId])

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!productionRoute || !isSuccess) {
    return <Navigate to={`/dashboard/${installationId}`} />
  }

  return (
    <>
      <div>
        <BreadcrumbsBar
          links={[
            { label: t('common:breadcrumbs.dashboard'), to: '/dashboard' },
            { label: productionRoute.name, to: '' }
          ]}
        />
        <Container>
          <header className="flex justify-between items-center">
            <H2>{productionRoute.name}</H2>
            <div className="flex gap-2">
              <Button
                variant="subtle"
                sizeType="sm"
                icon={<PencilIcon />}
                onClick={openRenameDialog}
              >
                {t('common:actions.rename')}
              </Button>
              {reportsNotDeleted.length > 0 ? (
                <Tooltip content={<p className="text-center">{tooltipString}</p>} position="bottom">
                  <Button
                    variant="danger"
                    sizeType="sm"
                    icon={<TrashIcon />}
                    disabled={isDeleteButtonDisabled}
                  >
                    {t('common:actions.delete')}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  variant="danger"
                  sizeType="sm"
                  icon={<TrashIcon />}
                  onClick={openDeleteDialog}
                >
                  {t('common:actions.delete')}
                </Button>
              )}
            </div>
          </header>
          <ProductionRouteSummary productionRoute={productionRoute} />
        </Container>
      </div>
      <ProductionRouteRenameDialog
        name={productionRoute.name}
        isOpen={isRenameDialogOpen}
        close={closeRenameDialog}
        isUpdating={isUpdatingProductionRoute}
        onUpdate={onUpdateProductionRouteName}
      />
      <ProductionRouteDeleteDialog
        name={productionRoute.name}
        isOpen={isDeleteDialogOpen}
        close={closeDeleteDialog}
        isDeleting={isDeletingProductionRoute}
        onDelete={onDeleteProductionRoute}
      />
    </>
  )
}
